import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface SystemSettings {
  exchange_rate?: string;
  bonus_percent?: string;
  affiliate_rate?: string;
  signup_enabled?: string;
  signup_fields_config?: string;
  kyc_warning_enabled?: string;
  require_kyc_for_deposit?: string;
  require_kyc_for_withdraw?: string;
  require_kyc_for_transfer?: string;
  require_otp_for_phone_signup?: string;
  require_email_verification?: string;
  savings_interest_rate?: string;
  fixed_deposit_rates?: string;
  interest_calculation_enabled?: string;
  interest_payment_notification?: string;
  deduct_on_account_purchase?: string;
  
  // Interest payment scheduling settings
  savings_payment_frequency?: string;
  fixed_deposit_payment_frequency?: string;
  monthly_payment_day?: string;
  weekly_payment_day?: string;
  biweekly_payment_days?: string;
  quarterly_payment_day?: string;
  semiannual_payment_day?: string;
  annual_payment_date?: string;
  
  // Additional settings for future use
  [key: string]: string | undefined;
}

interface SystemSettingsContextType {
  settings: SystemSettings;
  loading: boolean;
  refreshSettings: () => Promise<void>;
  updateSetting: (key: string, value: string) => Promise<void>;
}

const SystemSettingsContext = createContext<SystemSettingsContextType | undefined>(undefined);

export const useSystemSettings = () => {
  const context = useContext(SystemSettingsContext);
  if (context === undefined) {
    throw new Error('useSystemSettings must be used within a SystemSettingsProvider');
  }
  return context;
};

interface SystemSettingsProviderProps {
  children: ReactNode;
}

export const SystemSettingsProvider: React.FC<SystemSettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<SystemSettings>({});
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchSettings = async () => {
    try {
      console.log('🔄 Fetching system settings...');
      
      const { data, error } = await supabase
        .from('system_settings')
        .select('key, value');

      if (error) {
        // Only log in development to keep console clean during audits
        if (import.meta.env.DEV) {
          console.error('❌ Supabase error fetching settings:', error);
        }
        throw error;
      }

      if (data) {
        const settingsMap = data.reduce((acc, item) => {
          acc[item.key] = item.value;
          return acc;
        }, {} as SystemSettings);

        setSettings(settingsMap);
        console.log('✅ System settings loaded:', settingsMap);
        
        // เก็บใน localStorage เป็น fallback
        try {
          localStorage.setItem('system_settings_cache', JSON.stringify(settingsMap));
          localStorage.setItem('system_settings_cache_time', Date.now().toString());
        } catch (e) {
          console.warn('Could not cache settings to localStorage:', e);
        }
      }
    } catch (error: any) {
      // Only log in development to keep console clean during audits
      if (import.meta.env.DEV) {
        console.error('❌ Error fetching system settings:', error);
      }
      
      // ลองใช้ cached settings จาก localStorage
      try {
        const cached = localStorage.getItem('system_settings_cache');
        const cacheTime = localStorage.getItem('system_settings_cache_time');
        
        if (cached) {
          const parsedSettings = JSON.parse(cached);
          setSettings(parsedSettings);
          
          const cacheAge = Date.now() - parseInt(cacheTime || '0');
          const cacheAgeMinutes = Math.floor(cacheAge / 1000 / 60);
          
          console.log(`Using cached settings (${cacheAgeMinutes} minutes old)`);
          
          toast({
            title: "Loaded from cache",
            description: "Using cached settings (" + cacheAgeMinutes + " min ago)",
            variant: "default",
          });
          
          return; // ใช้ cache สำเร็จ ไม่ต้องแสดง error
        }
      } catch (cacheError) {
        console.error('Failed to load cached settings:', cacheError);
      }
      
      // ถ้าไม่มี cache ให้ใช้ค่า default
      const defaultSettings: SystemSettings = {
        signup_enabled: 'true',
        kyc_warning_enabled: 'false',
        require_kyc_for_deposit: 'false',
        require_kyc_for_withdraw: 'false',
        require_kyc_for_transfer: 'false',
      };
      
      setSettings(defaultSettings);
      console.log('⚠️ Using default settings (offline mode)');
      
      // แสดง error แต่ไม่ให้ app crash
      toast({
        title: "Offline mode",
        description: "Using basic settings - connection unstable",
        variant: "default",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key: string, value: string) => {
    try {
      const { error } = await supabase
        .from('system_settings')
        .upsert({ key, value }, { onConflict: 'key' });

      if (error) throw error;

      // Update local state immediately
      setSettings(prev => ({ ...prev, [key]: value }));
      
      console.log("✅ Setting updated: " + key + " = " + value);
    } catch (error) {
      console.error('Error updating setting:', error);
      toast({
        title: "Error",
        description: "Unable to update settings",
        variant: "destructive",
      });
      throw error;
    }
  };

  const refreshSettings = async () => {
    setLoading(true);
    await fetchSettings();
  };

  useEffect(() => {
    fetchSettings();

    // Set up real-time subscription for system_settings changes
    const channel = supabase
      .channel('system-settings-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'system_settings'
        },
        (payload) => {
          console.log('🔔 Real-time system settings change:', payload);
          
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            const { key, value } = payload.new as { key: string; value: string };
            setSettings(prev => ({ ...prev, [key]: value }));
            
          toast({
            title: "System settings updated",
            description: key + " = " + value,
          });
          } else if (payload.eventType === 'DELETE') {
            const { key } = payload.old as { key: string };
            setSettings(prev => {
              const newSettings = { ...prev };
              delete newSettings[key];
              return newSettings;
            });
          }
        }
      )
      .subscribe();

    // Cleanup subscription
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const value: SystemSettingsContextType = {
    settings,
    loading,
    refreshSettings,
    updateSetting,
  };

  return (
    <SystemSettingsContext.Provider value={value}>
      {children}
    </SystemSettingsContext.Provider>
  );
};